'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, ChevronRight } from 'lucide-react';

const MENU_ITEMS = {
  ru: [
    { name: 'Услуги', href: '/ru/services' },
    { name: 'Цены', href: '/ru/pricing' },
    { name: 'О нас', href: '/ru/about' },
    { name: 'Контакты', href: '/ru/contacts' },
  ],
  ro: [
    { name: 'Servicii', href: '/ro/services' },
    { name: 'Prețuri', href: '/ro/pricing' },
    { name: 'Despre noi', href: '/ro/about' },
    { name: 'Contacte', href: '/ro/contacts' },
  ],
  en: [
    { name: 'Services', href: '/en/services' },
    { name: 'Pricing', href: '/en/pricing' },
    { name: 'About', href: '/en/about' },
    { name: 'Contacts', href: '/en/contacts' },
  ],
  uk: [
    { name: 'Послуги', href: '/uk/services' },
    { name: 'Ціни', href: '/uk/pricing' },
    { name: 'Про нас', href: '/uk/about' },
    { name: 'Контакти', href: '/uk/contacts' },
  ],
} as const;

const UI_TEXT = {
  ru: { order: 'Заказать перевод' },
  ro: { order: 'Comandă traducere' },
  en: { order: 'Order Translation' },
  uk: { order: 'Замовити переклад' },
} as const;

const LOCALES = ['ru', 'ro', 'en', 'uk'];

export default function Header({ lang }: { lang: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  const menu = MENU_ITEMS[lang as keyof typeof MENU_ITEMS] || MENU_ITEMS.ru;
  const t = UI_TEXT[lang as keyof typeof UI_TEXT] || UI_TEXT.ru;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    // Check scroll position on mount (fixes behavior on page refresh)
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Закрываем меню при смене страницы
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Шапка теперь всегда имеет фон для читаемости, но меняет стиль при скролле
  const shouldShowBg = true; 

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/70 backdrop-blur-xl border-b border-blue-600/10 py-3 shadow-lg' 
        : 'bg-white border-b border-slate-100 py-5'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href={`/${lang}`} className="flex items-center space-x-2 group">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center transition-transform group-hover:rotate-12 duration-500">
            <span className="text-white font-black text-xl">T</span>
          </div>
          <span className={`text-2xl font-black tracking-tighter transition-colors ${
            scrolled ? 'text-slate-900' : 'text-slate-900' // Always dark for contrast on white bg sections
          }`}>
            TST<span className="text-blue-600">.md</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {menu.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`text-sm font-bold tracking-tight transition-all hover:text-blue-600 ${
                  isActive ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Right Action Area */}
        <div className="flex items-center space-x-6">
          {/* Language Switcher */}
          <div className="hidden sm:flex items-center bg-slate-100 rounded-full p-1 border border-slate-200">
            {LOCALES.map((l) => (
              <Link
                key={l}
                href={pathname.replace(`/${lang}`, `/${l}`)}
                className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                  lang === l
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {l}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            href={`/${lang}/contacts`}
            className="hidden lg:flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-full text-sm font-bold hover:bg-blue-700 transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/20"
          >
            {t.order}
          </Link>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2 text-slate-900 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 top-[72px] bg-white z-40 transition-transform duration-500 md:hidden ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col p-8 space-y-6">
          {menu.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="text-3xl font-black text-slate-900 flex items-center justify-between group"
            >
              {item.name}
              <ChevronRight className="text-slate-300 group-hover:text-blue-600 transition-transform group-hover:translate-x-2" />
            </Link>
          ))}
          
          <div className="pt-10 border-t border-slate-100">
            <div className="flex flex-wrap gap-4 mb-8">
              {LOCALES.map((l) => (
                <Link
                  key={l}
                  href={pathname.replace(`/${lang}`, `/${l}`)}
                  className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border transition-all ${
                    lang === l
                      ? 'bg-blue-50 border-blue-200 text-blue-600'
                      : 'bg-white border-slate-200 text-slate-400'
                  }`}
                >
                  {l}
                </Link>
              ))}
            </div>
            
            <Link
              href={`/${lang}/contacts`}
              className="w-full flex items-center justify-center p-5 bg-blue-600 text-white rounded-[2rem] text-lg font-black"
            >
              {t.order}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
